<!-- @catalog Labelled text / number / textarea input with validation. The default form text field. -->
<template>
  <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
    <c-label
      v-if="label"
      :label="label"
      :tooltip="tooltip"
      :required="required"
      :for="resolvedFor"
    />

    <q-input
      v-bind="$attrs"
      ref="inputRef"
      v-model="model"
      :required="required"
      :for="resolvedFor"
      outlined
      :class="[classes, requiredAndEmpty, _showPlaceholderOnFocus, _singleLine]"
      class="c-input tw-relative tw-overflow-hidden"
    >
      <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope || {}" />
      </template>

      <div
        class="custom-input__check tw-absolute -tw-right-[12px] tw-top-0 tw-flex tw-h-[24px] tw-w-[24px] tw-items-center tw-justify-center tw-rounded-bl tw-rounded-tr tw-p-1"
        :class="feedbackIconClasses"
      >
        <q-icon :name="feedbackIcon" class="custom-input__check__icon" />
      </div>
    </q-input>
  </div>
</template>

<script setup>
import { computed, watch, ref, useAttrs, useTemplateRef } from "vue";
import { debounce } from "quasar";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "~/tailwind.config.js";
import CLabel from "shared/components/wrappers/label/CLabel.vue";

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

// Define the model
const model = defineModel({
  required: true,
  type: [String, Number, null],
  default: () => "",
});

// Ref
const inputRef = useTemplateRef("inputRef");

// Define props
const props = defineProps({
  feedback: {
    type: Object,
    default: {},
  },
  labelSize: {
    type: String,
    default: "base",
    validator: (value) => {
      const {
        theme: { fontSize },
      } = resolveConfig(tailwindConfig);

      return Object.keys(fontSize).includes(value);
    },
  },
  height: {
    type: String,
    default: "default",
    validator: (value) => {
      return ["xs", "sm", "md", "lg", "default"].includes(value);
    },
  },
  showPlaceholderOnFocus: {
    type: Boolean,
    default: false,
  },
  // Renders an autogrow textarea as a compact 40px one-line field (the
  // rating-row look) instead of the 70px form textarea; it still grows with
  // multi-line content. Opt-in: existing autogrow inputs keep the form look.
  singleLine: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  // Info icon with explanation next to the label (forwarded to CLabel).
  tooltip: {
    type: [String, null],
    default: () => "",
  },
  required: {
    type: Boolean,
    default: false,
  },
  for: {
    type: [String, null],
    default: null,
  },
});

// Stable per-instance fallback id, used when neither `for` nor `name` is provided.
const generatedFor = `c-input-${Math.random().toString(36).slice(2, 11)}`;

// Resolves the id used for both the <c-label for=""> and the inner <input id="">.
// Priority: explicit `for` prop > forwarded `name` attribute > generated fallback.
const resolvedFor = computed(() => {
  if (props.for) {
    return props.for;
  }

  if (typeof attrs.name === "string" && attrs.name.length > 0) {
    return attrs.name;
  }

  return generatedFor;
});

// Initialize the tailwindConfig file
const { theme } = resolveConfig(tailwindConfig);

// Refs
const currentFeedback = ref({});

// Return the correct icon based on the feedbackType
const feedbackIcon = computed(() => {
  const icon = currentFeedback.value.type === "error" ? "fa-xmark" : "fa-check";

  return `fa-solid ${icon}`;
});

// Add the required class if the input is required and empty
const requiredAndEmpty = computed(() => {
  if (props.required && model.value === "") {
    return "required";
  }
  return false;
});

const _showPlaceholderOnFocus = computed(() => {
  return props.showPlaceholderOnFocus ? "show-placeholder-on-focus" : false;
});

const _singleLine = computed(() => {
  return props.singleLine ? "single-line" : false;
});

const classes = computed(() => {
  if (Object.keys(currentFeedback.value).length) {
    return currentFeedback.value?.type === "error"
      ? "feedback-error"
      : "feedback-success";
  }

  return "";
});

// Conditionally add classes based on the feedback
const feedbackIconClasses = computed(() => {
  return [
    ...(currentFeedback.value.type ? ["is-active"] : []),
    ...(currentFeedback.value.type === "error" ? ["tw-bg-red-500"] : []),
    ...(currentFeedback.value.type === "success" ? ["tw-bg-green-500"] : []),
  ];
});

// Return the scss variable specified in our tailwind config
// based on the labelSize property
const labelClass = computed(() => {
  const { fontSize } = theme;

  return fontSize?.[props.labelSize]
    ? fontSize[props.labelSize][0]
    : fontSize["base"][0];
});

// Return the height of the textarea
const inputHeight = computed(() => {
  switch (props.height) {
    case "xs":
      return "18px";
    case "sm":
      return "46px";
    case "md":
      return "56px";
    case "lg":
      return "200px";
    default:
      return "70px";
  }
});

// Return the height for single-line (non-textarea) inputs. `default` keeps the
// standard 40px; the larger tokens give a fixed control height.
const controlHeight = computed(() => {
  switch (props.height) {
    case "xs":
      return "32px";
    case "sm":
      return "36px";
    case "md":
      return "40px";
    case "lg":
      return "48px";
    default:
      return "40px";
  }
});

// Remove the errors from the input
const removeFeedback = debounce(() => {
  if (currentFeedback.value.type === "error") {
    return;
  }

  currentFeedback.value = {};
}, 3000);

// Watch errors
watch(
  () => props.feedback,
  (feedback) => {
    currentFeedback.value = feedback;

    removeFeedback();
  },
);

defineExpose({
  inputRef,
});
</script>

<style lang="scss">
.show-arrows {
  @apply tw-relative;

  input[type="number"],
  input[type="number"] {
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      opacity: 1;
    }

    &::-webkit-inner-spin-button {
      @apply tw-absolute tw-right-0 tw-top-0 tw-top-1/2 tw-h-[70%] -tw-translate-y-1/2;
    }
  }
}

label.q-field {
  &.q-field--outlined .q-field__control::before {
    border-color: rgb(var(--color-grey-200));
  }

  &.q-textarea {
    textarea.q-field__native {
      padding-top: 6px;
      min-height: v-bind(inputHeight);
    }
  }

  // Single-line mode (`single-line` prop): an autogrow textarea that renders
  // as a compact 40px one-line field and only grows past that with content.
  // Out-specifies the q-textarea default above, so no !important is needed.
  &.single-line.q-textarea {
    // Outer field box stays single-line at 40px; autogrow expands it past
    // this min as the user types.
    .q-field__control {
      border-radius: 8px;
      min-height: 40px;
    }

    // Quasar adds vertical padding on the control-container for autogrow
    // textareas (2px top + 2px bottom by default), which pushes the field
    // to 44px. Zeroing it makes container = textarea height; min-height: 40
    // keeps the floor consistent with the outer control.
    .q-field__control-container {
      min-height: 40px;
      padding-top: 0;
      padding-bottom: 0;
    }

    // Make the textarea fill the 40px control on the empty / single-line
    // state, with symmetric padding so the line sits dead-center.
    // 40 (box) − 18 (Quasar's textarea line-height) = 22 of slack, split
    // evenly → 11px top + 11px bottom puts the text centroid at y=20.
    // Multi-line content still grows naturally: autogrow measures
    // scrollHeight (padding + line stack + padding) and sets `height` past
    // 40px, container + control follow.
    textarea.q-field__native {
      min-height: 40px;
      padding-top: 11px;
      padding-bottom: 11px;
      resize: none;
    }
  }

  // Single-line c-inputs are sized via the `height` prop (default 40px).
  // Textareas keep their own height controlled by the `height` prop above.
  &.c-input:not(.q-textarea) {
    .q-field__control {
      min-height: v-bind(controlHeight);
      height: v-bind(controlHeight);
    }

    .q-field__native {
      min-height: v-bind(controlHeight);
    }

    .q-field__marginal {
      height: v-bind(controlHeight);
    }
  }

  .q-field {
    &__label {
      font-size: v-bind(labelClass);
    }
  }

  &.feedback-success {
    .q-field__control {
      @apply tw-bg-green-50;

      &::after {
        @apply tw-border-green-500 focus:tw-border-green-500;
      }
    }
  }

  &.feedback-error {
    .q-field__control {
      @apply tw-bg-red-50;

      &::after {
        @apply tw-border-red-500 focus:tw-bg-red-500;
      }
    }
  }

  // Don't show the check icon if the input is in an error state
  &.q-field--error {
    .custom-input__check {
      display: none;
    }

    .q-field__control {
      &::after {
        @apply tw-border-[currentColor];
      }
    }
  }

  .custom-input__check {
    @apply tw-overflow-hidden tw-transition-all tw-duration-200;
    transform: translate3d(102%, -102%, 0);

    &.is-active {
      transform: translate3d(0, 0, 0);
    }

    &__icon {
      @apply tw-text-white;
    }
  }

  .q-field__control-container input[required] + .q-field__label:after {
    content: " *";
  }

  &.q-field--error {
    .q-field__control {
      @apply tw-bg-red-50;
    }
  }

  &.show-placeholder-on-focus {
    .q-field__native::placeholder {
      opacity: 0;
    }

    .q-field__native:focus::placeholder {
      opacity: 0.7;
    }
  }
}
</style>
